<template>
  <div class="acceptance-tab">
    <div class="acceptance-tab__filters">
      <div class="acceptance-tab__filter-item">
        <CustomSelect
          :options="workSetCheckGroups"
          label="Группа работ"
          option-label-key="work_set_check_group_name"
          option-value-key="work_set_check_group_id"
          @change="value => onFilterChange('work_set_check_group_id', value)"
          :value="filters.work_set_check_group_id"
          :loading="isLoadingGroups"
        />
      </div>
      <div class="acceptance-tab__filter-item">
        <CustomSelect
          :options="assignTypes"
          label="Исполнитель"
          option-label-key="assign_type_name"
          option-value-key="assign_type_id"
          @change="value => onFilterChange('assign_type_id', value)"
          :value="filters.assign_type_id"
          :loading="isLoadingAssignTypes"
        />
      </div>
      <div class="acceptance-tab__filter-item acceptance-tab__filter-item--search">
        <CustomInput
          :value="filters.search_text"
          :isSearch="true"
          placeholder="Поиск"
          class="acceptance-tab__search"
          v-debounce:300="onSearchChange"
        />
      </div>
    </div>

    <div class="acceptance-groups">
      <div class="acceptance-groups__toolbar">
        <span class="acceptance-groups__toolbar-title">{{ tableTitle }}</span>
        <q-btn
          size="sm"
          title="Добавить"
          color="primary"
          dense
          round
          icon="add"
          :disable="loading"
          @click="openModal()"
        />
      </div>

      <q-linear-progress v-if="loading" indeterminate class="acceptance-groups__progress" />

      <div class="acceptance-groups__body">
        <NotFoundText v-if="!loading && !groups.length" />

        <q-list v-else-if="groups.length" class="acceptance-groups__list" bordered separator>
        <q-expansion-item
          v-for="(group, index) in groups"
          :key="groupExpansionKey(group, index)"
          :default-opened="index === 0"
          expand-icon="keyboard_arrow_down"
          class="acceptance-groups__expansion"
          header-class="acceptance-groups__expansion-header"
        >
          <template #header>
            <q-item-section>
              <div class="acceptance-groups__header-label">
                {{ group.work_set_check_group_name || '—' }}
                <span class="acceptance-groups__header-count">({{ (group.steps || []).length }})</span>
              </div>
            </q-item-section>
          </template>

          <div class="acceptance-groups__table-wrap">
            <CustomTable
              title=""
              row-key="acceptance_id"
              :rowsData="getRowsWithGroup(group)"
              :columns="acceptanceGroupStepsTableColumns"
              :stickyHeader="true"
              :loading="false"
              :custom-body="true"
              :custom-header="true"
              :small="true"
            >
              <template #customHeader="props">
                <q-tr :props="props">
                  <q-th v-for="col in props.cols" :key="col.name" :props="props">
                    <template v-if="col.name === 'actions'">
                      <q-btn
                        size="sm"
                        title="Добавить"
                        color="primary"
                        dense
                        round
                        icon="add"
                        @click="openModal(null, group)"
                      />
                    </template>
                    <template v-else>{{ col.label }}</template>
                  </q-th>
                </q-tr>
              </template>

              <template #customBody="props">
                <q-tr>
                  <custom-table-cell align="center" vertical-align="center">
                    {{ props.row.acceptance_id }}
                  </custom-table-cell>
                  <custom-table-cell align="left" vertical-align="center">
                    {{ props.row.acceptance_step_name || '-' }}
                  </custom-table-cell>
                  <custom-table-cell align="left" vertical-align="center">
                    {{ props.row.assign_type_name || '-' }}
                  </custom-table-cell>
                  <custom-table-cell align="center" vertical-align="center">
                    {{ props.row.order_num }}
                  </custom-table-cell>
                  <custom-table-cell align="center" vertical-align="center">
                    <div class="action-buttons">
                      <q-btn
                        color="primary-light"
                        flat
                        dense
                        round
                        size="sm"
                        icon="edit"
                        @click="openModal(props.row)"
                      >
                        <q-tooltip>Редактировать</q-tooltip>
                      </q-btn>
                      <q-btn
                        color="negative"
                        flat
                        dense
                        round
                        size="sm"
                        icon="delete"
                        :loading="!!deleteLoading[props.row.acceptance_id]"
                        @click="confirmDelete(props.row)"
                      >
                        <q-tooltip>Удалить</q-tooltip>
                      </q-btn>
                    </div>
                  </custom-table-cell>
                </q-tr>
              </template>
            </CustomTable>
          </div>
        </q-expansion-item>
        </q-list>
      </div>
    </div>

    <q-dialog v-model="modalShow" persistent>
      <q-card class="acceptance-modal">
        <q-card-section class="acceptance-modal__header">
          <div class="acceptance-modal__title">
            {{ editing ? 'Редактировать приёмку' : 'Добавить приёмку' }}
          </div>
          <q-btn icon="close" flat round dense v-close-popup class="acceptance-modal__close" />
        </q-card-section>

        <q-card-section class="acceptance-modal__content">
          <q-form @submit.prevent="save" class="acceptance-form">
            <div class="acceptance-form__section">
              <div class="acceptance-form__item">
                <CustomSelect
                  label="Шаг приёмки"
                  :options="stepsForSelect"
                  option-label-key="acceptance_step_name"
                  option-value-key="acceptance_step_id"
                  :value="form.acceptance_step_id"
                  @change="form.acceptance_step_id = $event"
                  placeholder="Выберите шаг приёмки"
                  :required="true"
                />
              </div>
              <div class="acceptance-form__item">
                <CustomSelect
                  label="Группа работ"
                  :options="workSetCheckGroups"
                  option-label-key="work_set_check_group_name"
                  option-value-key="work_set_check_group_id"
                  :value="form.work_set_check_group_id"
                  @change="form.work_set_check_group_id = $event"
                  :loading="isLoadingGroups"
                  placeholder="Выберите группу работ"
                  :required="true"
                />
              </div>
              <div class="acceptance-form__item">
                <CustomSelect
                  label="Тип назначения"
                  :options="assignTypes"
                  option-label-key="assign_type_name"
                  option-value-key="assign_type_id"
                  :value="form.assign_type_id"
                  @change="form.assign_type_id = $event"
                  :loading="isLoadingAssignTypes"
                  placeholder="Выберите тип назначения"
                  :required="true"
                />
              </div>
              <div class="acceptance-form__item">
                <q-input v-model.number="form.order_num" label="Порядковый номер" dense outlined type="number"
                         :rules="[v => v !== null && v !== '' || 'Обязательное поле']" />
              </div>
            </div>

            <div class="acceptance-modal__footer">
              <CustomButton @click="modalShow = false" :disabled="saving" outlined>Отмена</CustomButton>
              <CustomButton type="submit" :loading="saving"
                            :disabled="!form.acceptance_step_id || !form.work_set_check_group_id || !form.assign_type_id">
                {{ editing ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog v-model="confirmDeleteDialog" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">Подтверждение удаления</div>
        </q-card-section>
        <q-card-section>Вы уверены, что хотите удалить эту запись?</q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDeleteDialog = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onDeleteConfirm" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomSelect from "@/components/form/CustomSelect";
import CustomInput from "@/components/form/CustomInput";
import CustomButton from "@/components/common/CustomButton.vue";
import NotFoundText from "@/components/common/NotFoundText.vue";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import {
  getAcceptanceSteps,
  getAcceptances,
  updAcceptance,
  deleteAcceptance,
  getWorkSetCheckGroups,
  getAssignTypes,
  acceptanceGroupStepsTableColumns,
} from "@/views/Acceptance/services";

const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();

/** API: группы { work_set_check_group_id, work_set_check_group_name, steps }; иначе — плоский список (группируем на клиенте). */
const normalizeAcceptanceGroups = (payload) => {
  if (!payload || !Array.isArray(payload)) return [];
  if (payload.length && Object.prototype.hasOwnProperty.call(payload[0], "steps")) {
    return payload;
  }
  const order = [];
  const buckets = new Map();
  for (const row of payload) {
    const gid = row.work_set_check_group_id;
    if (!buckets.has(gid)) {
      buckets.set(gid, {
        work_set_check_group_id: gid,
        work_set_check_group_name: row.work_set_check_group_name,
        steps: [],
      });
      order.push(gid);
    }
    const step = { ...row };
    delete step.work_set_check_group_id;
    delete step.work_set_check_group_name;
    buckets.get(gid).steps.push(step);
  }
  return order.map((id) => buckets.get(id));
};

const getRowsWithGroup = (group) =>
  (group.steps || []).map((step) => ({
    ...step,
    work_set_check_group_id: group.work_set_check_group_id,
    work_set_check_group_name: group.work_set_check_group_name,
  }));

const groupExpansionKey = (group, index) =>
  group.work_set_check_group_id != null ? String(group.work_set_check_group_id) : `idx-${index}`;

const filters = ref({
  search_text: '',
  work_set_check_group_id: null,
  assign_type_id: null,
});

const onFilterChange = (key, value) => {
  filters.value[key] = value || null;
  loadData();
};

const onSearchChange = (value) => {
  filters.value.search_text = value || '';
  loadData();
};

const groups = ref([]);
const loading = ref(false);
const modalShow = ref(false);
const editing = ref(null);
const saving = ref(false);
const deleteLoading = ref({});
const form = ref({
  acceptance_id: null,
  assign_type_id: null,
  work_set_check_group_id: null,
  acceptance_step_id: null,
  order_num: null,
});

const workSetCheckGroups = ref([]);
const assignTypes = ref([]);
const stepsForSelect = ref([]);
const isLoadingGroups = ref(false);
const isLoadingAssignTypes = ref(false);

const tableTitle = computed(() => {
  const group = workSetCheckGroups.value.find(
    g => g.work_set_check_group_id === filters.value.work_set_check_group_id
  );
  if (group) return `Приёмка — ${group.work_set_check_group_name}`;
  return 'Приёмка';
});

const loadData = async () => {
  loading.value = true;
  const params = {};
  if (filters.value.search_text) params.search_text = filters.value.search_text;
  if (filters.value.work_set_check_group_id) params.work_set_check_group_id = filters.value.work_set_check_group_id;
  if (filters.value.assign_type_id) params.assign_type_id = filters.value.assign_type_id;
  const res = await getAcceptances(params);
  loading.value = false;
  if (!res) return;
  groups.value = normalizeAcceptanceGroups(res);
};

const loadWorkSetCheckGroups = async () => {
  isLoadingGroups.value = true;
  const res = await getWorkSetCheckGroups();
  isLoadingGroups.value = false;
  if (!res) return;
  workSetCheckGroups.value = res || [];
};

const loadAssignTypes = async () => {
  isLoadingAssignTypes.value = true;
  const res = await getAssignTypes();
  isLoadingAssignTypes.value = false;
  if (!res) return;
  assignTypes.value = res || [];
};

const loadStepsForSelect = async () => {
  const res = await getAcceptanceSteps({});
  if (!res) return;
  stepsForSelect.value = (res || []).filter(s => s.is_active);
};

const openModal = (row = null, groupForAdd = null) => {
  editing.value = row;
  if (row) {
    form.value = {
      acceptance_id: row.acceptance_id,
      assign_type_id: row.assign_type_id,
      work_set_check_group_id: row.work_set_check_group_id,
      acceptance_step_id: row.acceptance_step_id,
      order_num: row.order_num,
    };
  } else {
    form.value = {
      acceptance_id: null,
      assign_type_id: null,
      work_set_check_group_id: groupForAdd?.work_set_check_group_id ?? null,
      acceptance_step_id: null,
      order_num: null,
    };
  }
  modalShow.value = true;
};

const save = async () => {
  if (!form.value.acceptance_step_id || !form.value.work_set_check_group_id || !form.value.assign_type_id) {
    showErrorSnackbar("Все обязательные поля должны быть заполнены");
    return;
  }
  saving.value = true;
  const body = {
    acceptance_id: editing.value?.acceptance_id || null,
    assign_type_id: form.value.assign_type_id,
    work_set_check_group_id: form.value.work_set_check_group_id,
    acceptance_step_id: form.value.acceptance_step_id,
    order_num: form.value.order_num,
  };
  const res = await updAcceptance(body);
  saving.value = false;
  if (!res) return;
  showSucceedSnackbar(editing.value ? "Запись обновлена" : "Запись добавлена");
  modalShow.value = false;
  await loadData();
};

const confirmDeleteDialog = ref(false);
const deleteTargetRow = ref(null);

const confirmDelete = (row) => {
  deleteTargetRow.value = row;
  confirmDeleteDialog.value = true;
};

const onDeleteConfirm = async () => {
  confirmDeleteDialog.value = false;
  if (!deleteTargetRow.value) return;
  const id = deleteTargetRow.value.acceptance_id;
  deleteLoading.value = { ...deleteLoading.value, [id]: true };
  const res = await deleteAcceptance(id);
  deleteLoading.value = { ...deleteLoading.value, [id]: false };
  if (!res) return;
  showSucceedSnackbar("Запись удалена");
  deleteTargetRow.value = null;
  await loadData();
};

onMounted(async () => {
  await Promise.all([
    loadData(),
    loadWorkSetCheckGroups(),
    loadAssignTypes(),
    loadStepsForSelect(),
  ]);
});
</script>

<style scoped lang="scss">
.acceptance-tab {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding-top: 12px;
}

.acceptance-tab__filters {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-shrink: 0;
}

.acceptance-tab__filter-item {
  width: 200px;

  &--search {
    width: 280px;
  }
}

.acceptance-tab__search {
  margin-top: 22px;
}

.acceptance-groups {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.acceptance-groups__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.acceptance-groups__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.acceptance-groups__toolbar-title {
  font-size: 18px;
  font-weight: 600;
  color: #373d48;
}

.acceptance-groups__progress {
  flex-shrink: 0;
  margin-bottom: 8px;
}

.acceptance-groups__list {
  border-radius: 4px;
}

.acceptance-groups__expansion-header {
  font-weight: 600;
}

.acceptance-groups__header-label {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.acceptance-groups__header-count {
  font-weight: 500;
  color: #6b7280;
  font-size: 14px;
}

.acceptance-groups__table-wrap {
  padding: 8px 12px 12px;
  background: #fafafa;
}

:deep(.acceptance-groups__table-wrap .custom-table) {
  flex: unset;
  min-height: 0;
}

:deep(.acceptance-groups__table-wrap .custom-table__container) {
  max-height: 360px;
}

.action-buttons {
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.acceptance-modal {
  min-width: 500px;
  max-width: 700px;
}

.acceptance-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.acceptance-modal__title {
  font-size: 20px;
  font-weight: 600;
  color: #373d48;
  line-height: 20px;
}

.acceptance-modal__close {
  color: #9ca3af;
}

.acceptance-modal__content {
  padding: 20px 24px;
}

.acceptance-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 15px;
}

.acceptance-form__section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.acceptance-form__item {
  width: 100%;
}

.acceptance-modal__footer {
  width: 100%;
  height: 46px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 15px;
}

@media (max-width: 600px) {
  .acceptance-modal {
    min-width: 100%;
  }
}
</style>
